import type { BriefCveMatch } from '../../data/types';
import { AgentBadge } from './AgentBadge';
import { cn } from '../../lib/utils';

interface CveRelevanceProps {
  cves: BriefCveMatch[];
  source: 'stub' | 'industryUpdates';
}

export function CveRelevance({ cves, source }: CveRelevanceProps) {
  return (
    <section className="lg:col-span-7 bg-bg-secondary border border-border-default rounded-xl overflow-hidden flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border-light">
        <h3 className="font-sans font-medium text-[12.5px] text-text-primary m-0">
          CVEs touching your tenants
        </h3>
        <span className="font-mono text-[10.5px] text-text-secondary px-1.5 py-px bg-bg-tertiary rounded border border-border-default">
          cross-referenced
        </span>
      </header>

      {cves.length === 0 ? (
        <div className="flex-1 px-4 py-10 text-center">
          <div className="text-text-secondary text-sm font-sans">CVE feed not yet wired.</div>
          <div className="text-text-muted text-xs mt-1">
            {source === 'stub'
              ? 'Will be sourced from IndustryUpdates once the join from Customers.industry is validated.'
              : 'No high-severity matches in the last refresh.'}
          </div>
        </div>
      ) : (
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr>
              {['ID', 'CVSS', 'Title', 'Affects', 'Action'].map(h => (
                <th
                  key={h}
                  className="text-left px-3 py-2 font-sans text-[9.5px] uppercase tracking-[0.16em] text-text-secondary bg-bg-tertiary border-b border-border-light font-bold"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cves.map((c, i) => (
              <tr
                key={c.id}
                className={cn(i !== cves.length - 1 && 'border-b border-border-light')}
              >
                <td className="px-3 py-2.5 text-text-primary font-mono">{c.id}</td>
                <td
                  className={cn(
                    'px-3 py-2.5 font-mono font-semibold',
                    c.cvss >= 9 ? 'text-accent-red' : c.cvss >= 7 ? 'text-accent-orange' : 'text-brand-blue'
                  )}
                >
                  {c.cvss}
                </td>
                <td className="px-3 py-2.5 text-text-primary">{c.title}</td>
                <td className="px-3 py-2.5 text-text-primary">
                  <div className="flex gap-1 flex-wrap">
                    {c.tenants.map(t => (
                      <span
                        key={t}
                        className="font-mono text-[10px] bg-bg-tertiary border border-border-default px-1.5 py-px rounded text-text-secondary"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-text-secondary">{c.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="px-4 py-2 border-t border-border-light text-[11px] text-text-secondary font-sans flex items-center gap-2 bg-bg-tertiary/40">
        <AgentBadge name="Hunt" /> matches disclosed CVEs against your tenant inventory.
      </footer>
    </section>
  );
}
